import emailjs from '@emailjs/browser';

// EmailJS Configuration
const EMAILJS_CONFIG = {
  serviceId: 'service_56gbqtj',
  welcomeTemplateId: 'template_nunlov7',
  resetPasswordTemplateId: 'template_pa6e1g7',
  loginAlertTemplateId: 'template_axtqalt',
  publicKey: '3EKQKmlOIiCUImLop'
};

const PAYMENT_CONFIG   = { serviceId: 'service_qeksltt', templateId: 'template_5wc28lo', publicKey: '3EKQKmlOIiCUImLop' };
const WALLET_CONFIG    = { serviceId: 'service_qeksltt', templateId: 'template_a3qnhvn', publicKey: '3EKQKmlOIiCUImLop' };
const SHIPMENT_CONFIG  = { serviceId: 'service_y7hm0dq', templateId: 'template_z7svkc7', publicKey: '3EKQKmlOIiCUImLop' };
const BOOKING_CONFIG   = { serviceId: 'service_xfg1tg9', templateId: 'template_mvuddm6', publicKey: '3EKQKmlOIiCUImLop' };

/**
 * Send welcome email to newly created user with create password link
 * @param {Object} userData - User data object
 * @param {string} userData.name - User's full name
 * @param {string} userData.email - User's email address
 * @param {string} userData.username - User's username
 * @returns {Promise} EmailJS response
 */
export const sendWelcomeEmail = async (userData) => {
  try {
    console.log('📧 Attempting to send welcome email...');
    console.log('User data:', { name: userData.name, email: userData.email });
    
    // Generate create password link (email encoded in base64)
    const encodedEmail = btoa(userData.email); // Base64 encode email
    const appUrl = process.env.REACT_APP_URL || window.location.origin;
    const createPasswordLink = `${appUrl}/create-password?token=${encodedEmail}`;
    
    // Prepare template parameters
    const templateParams = {
      to_email: userData.email,
      user_name: userData.name,
      user_email: userData.email,
      username: userData.username,
      app_name: "QuickLook",
      company_name: "QuickLook",
      reset_url: createPasswordLink,
      current_year: new Date().getFullYear(),
      support_email: "support@quicklook.com"
    };
    
    console.log('Create password link:', createPasswordLink);
    console.log('Template params:', templateParams);
    console.log('EmailJS config:', {
      serviceId: EMAILJS_CONFIG.serviceId,
      templateId: EMAILJS_CONFIG.welcomeTemplateId,
      publicKey: EMAILJS_CONFIG.publicKey.substring(0, 5) + '...'
    });

    // Send email using EmailJS
    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.welcomeTemplateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    );

    console.log('✅ Welcome email sent successfully:', response);
    return { success: true, response, createPasswordLink };
  } catch (error) {
    console.error('❌ Failed to send welcome email:', error);
    console.error('Error details:', {
      message: error.message,
      text: error.text,
      status: error.status
    });
    return { success: false, error: error.message || error.text || 'Unknown error' };
  }
};

/**
 * Initialize EmailJS (call this once in your app)
 */
export const initEmailJS = () => {
  emailjs.init(EMAILJS_CONFIG.publicKey);
  console.log('EmailJS initialized');
};

/**
 * Get approximate location from IP address
 * @returns {Promise<{state: string, country: string}>}
 */
const getLoginLocation = async () => {
  try {
    const res = await fetch('https://ipapi.co/json/');
    const data = await res.json();
    return {
      state: data.region || data.city || 'Unknown',
      country: data.country_name || 'Unknown'
    };
  } catch (e) {
    return { state: 'Unknown', country: 'Unknown' };
  }
};

/**
 * Get browser name and OS/device from user agent
 * @returns {{browser: string, device: string}}
 */
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown Browser';
  let device = 'Unknown Device';

  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\/|Opera/.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';

  if (/Windows/.test(ua)) device = 'Windows PC';
  else if (/Macintosh/.test(ua)) device = 'Mac';
  else if (/iPhone/.test(ua)) device = 'iPhone';
  else if (/iPad/.test(ua)) device = 'iPad';
  else if (/Android/.test(ua)) device = 'Android Device';
  else if (/Linux/.test(ua)) device = 'Linux PC';

  return { browser, device };
};

/**
 * Send login alert email with location, device, browser and reset link
 * @param {Object} userData
 * @param {string} userData.name - User's full name
 * @param {string} userData.email - User's email
 * @returns {Promise}
 */
export const sendLoginAlertEmail = async (userData) => {
  try {
    console.log('📧 Sending login alert email...');

    const { state, country } = await getLoginLocation();
    const { browser, device } = getDeviceInfo();

    const now = new Date();
    const loginTime = now.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }) + ' IST';

    const encodedEmail = btoa(userData.email);
    const resetPasswordLink = `https://quicklook.in/reset-password?token=${encodedEmail}`;

    const templateParams = {
      to_email: userData.email,
      user_name: userData.name || 'User',
      device,
      browser,
      state,
      country,
      login_time: loginTime,
      reset_password_link: resetPasswordLink
    };

    console.log('Login alert template params:', templateParams);

    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.loginAlertTemplateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    );

    console.log('✅ Login alert email sent:', response);
    return { success: true, response };
  } catch (error) {
    console.error('❌ Failed to send login alert email:', error);
    return { success: false, error: error.message || error.text || 'Unknown error' };
  }
};

/**
 * Send password reset email with unique link
 * @param {Object} emailData - Email data object
 * @param {string} emailData.email - User's email address
 * @param {string} emailData.name - User's name
 * @param {string} emailData.resetToken - Unique reset token
 * @returns {Promise} EmailJS response
 */
export const sendPasswordResetEmail = async (emailData) => {
  try {
    console.log('📧 Attempting to send password reset email...');
    console.log('Email data:', { email: emailData.email, name: emailData.name });
    
    // Generate reset link with token (email encoded in base64)
    const encodedEmail = btoa(emailData.email); // Base64 encode email
    const appUrl = process.env.REACT_APP_URL || window.location.origin;
    const resetLink = `${appUrl}/reset-password?token=${encodedEmail}`;
    
    // Prepare template parameters for password reset
    const templateParams = {
      to_email: emailData.email,
      user_name: emailData.name || 'User',
      app_name: "QuickLook",
      company_name: "QuickLook",
      reset_url: resetLink,
      current_year: new Date().getFullYear(),
      support_email: "support@quicklook.com",
      expiry_time: "24 hours"
    };
    
    console.log('Reset link:', resetLink);
    console.log('Template params:', templateParams);
    console.log('EmailJS config:', {
      serviceId: EMAILJS_CONFIG.serviceId,
      templateId: EMAILJS_CONFIG.resetPasswordTemplateId,
      publicKey: EMAILJS_CONFIG.publicKey.substring(0, 5) + '...'
    });

    // Send email using EmailJS with password reset template
    const response = await emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.resetPasswordTemplateId,
      templateParams,
      EMAILJS_CONFIG.publicKey
    );

    console.log('✅ Password reset email sent successfully:', response);
    return { success: true, response, resetLink };
  } catch (error) {
    console.error('❌ Failed to send password reset email:', error);
    console.error('Error details:', {
      message: error.message,
      text: error.text,
      status: error.status
    });
    return { success: false, error: error.message || error.text || 'Unknown error' };
  }
};

const fmt = (d) => new Date(d || Date.now()).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) + ' IST';

export const sendPaymentEmail = async ({
  to_email, name, payment_status, order_id, amount,
  razorpay_payment_id = '', payment_method = '', failure_reason = '',
  action_message = '', retry_payment_link = '', company_name = 'QuickLook'
}) => {
  try {
    await emailjs.send(PAYMENT_CONFIG.serviceId, PAYMENT_CONFIG.templateId, {
      to_email, name: name || 'User', payment_status,
      order_id: order_id || '', amount: amount || '',
      razorpay_payment_id, payment_method, payment_date: fmt(),
      failure_reason, action_message, retry_payment_link, company_name,
      message: payment_status === 'Successful'
        ? `Your payment of ${amount} was successful.`
        : `Your payment of ${amount} failed. ${failure_reason}`,
    }, PAYMENT_CONFIG.publicKey);
    return { success: true };
  } catch (error) {
    console.error('❌ Payment email failed:', error);
    return { success: false, error: error.message || error.text };
  }
};

export const sendWalletUpdateEmail = async ({
  to_email, name, transaction_type, amount, transaction_id = '',
  wallet_balance = '', description = '', company_name = 'QuickLook'
}) => {
  try {
    await emailjs.send(WALLET_CONFIG.serviceId, WALLET_CONFIG.templateId, {
      to_email, name: name || 'User', transaction_type, amount, transaction_id,
      wallet_balance, transaction_date: fmt(),
      description: description || `Wallet ${transaction_type.toLowerCase()} of ${amount}`,
      message: `₹${amount} has been ${transaction_type.toLowerCase()}ed to your QuickLook wallet.`,
      company_name,
    }, WALLET_CONFIG.publicKey);
    return { success: true };
  } catch (error) {
    console.error('❌ Wallet update email failed:', error);
    return { success: false, error: error.message || error.text };
  }
};

export const sendShipmentUpdateEmail = async ({
  to_email, name, order_id, tracking_id = '', courier_name = '',
  shipment_status, expected_delivery = '', tracking_link = '',
  company_name = 'QuickLook'
}) => {
  try {
    await emailjs.send(SHIPMENT_CONFIG.serviceId, SHIPMENT_CONFIG.templateId, {
      to_email, name: name || 'User', order_id, tracking_id, courier_name,
      shipment_status, update_date: fmt(), expected_delivery, tracking_link,
      message: `Your shipment ${order_id} status has been updated to: ${shipment_status}.`,
      company_name,
    }, SHIPMENT_CONFIG.publicKey);
    return { success: true };
  } catch (error) {
    console.error('❌ Shipment update email failed:', error);
    return { success: false, error: error.message || error.text };
  }
};

export const sendBookingStatusEmail = async ({
  to_email, name, booking_id, service_name = '', booking_date = '',
  booking_status, amount = '', booking_link = '', company_name = 'QuickLook'
}) => {
  try {
    await emailjs.send(BOOKING_CONFIG.serviceId, BOOKING_CONFIG.templateId, {
      to_email, name: name || 'User',
      booking_id: String(booking_id || ''), service_name, booking_date,
      updated_date: fmt(), booking_status, amount,
      booking_link: booking_link || 'https://quicklook.in/user/new-booking',
      message: `Your booking #${booking_id} has been ${booking_status.toLowerCase()}.`,
      company_name,
    }, BOOKING_CONFIG.publicKey);
    return { success: true };
  } catch (error) {
    console.error('❌ Booking status email failed:', error);
    return { success: false, error: error.message || error.text };
  }
};
